import { useEffect, useMemo, useRef, useState } from "react";
import { mediaApi, type MealLog } from "../api/client";
import { resizeImageToJpegBlob } from "../lib/media";

const MEDIA_LIMIT = 5;

interface Props {
  log: MealLog;
  onChanged: () => Promise<void> | void;
}

export function MealLogMedia({ log, onChanged }: Props) {
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewingMediaId, setPreviewingMediaId] = useState<string | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturingPhoto, setCapturingPhoto] = useState(false);

  const mediaIds = useMemo(
    () => log.media.map((media) => media.id),
    [log.media],
  );
  const hasReachedMediaLimit = mediaIds.length >= MEDIA_LIMIT;
  const remainingSlots = MEDIA_LIMIT - mediaIds.length;

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    if (mediaIds.length === 0) {
      setPreviewUrls({});
      return;
    }

    Promise.all(
      mediaIds.map(async (id) => {
        const result = await mediaApi.get(id);
        const objectUrl = URL.createObjectURL(result);
        objectUrls.push(objectUrl);
        return [id, objectUrl] as const;
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        setPreviewUrls(Object.fromEntries(entries));
      })
      .catch((fetchError) => {
        console.error(fetchError);
        if (!cancelled) {
          setError("写真の読み込みに失敗しました。");
        }
      });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [mediaIds]);

  useEffect(() => {
    return () => {
      stopCameraStream(cameraStreamRef.current);
    };
  }, []);

  useEffect(() => {
    if (previewingMediaId && !mediaIds.includes(previewingMediaId)) {
      setPreviewingMediaId(null);
    }
  }, [mediaIds, previewingMediaId]);

  const uploadFile = async (file: File) => {
    const blob = await resizeImageToJpegBlob(file);
    const upload = await mediaApi.createUploadUrl({
      meal_log_id: log.id,
      content_type: blob.type,
    });
    await mediaApi.upload(upload.media_id, blob);
    await mediaApi.confirm(upload.media_id);
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    if (hasReachedMediaLimit) {
      setError(`写真は${MEDIA_LIMIT}枚まで登録できます。`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    const skippedCount = files.length - filesToUpload.length;

    setError(null);
    setUploading(true);
    try {
      for (const file of filesToUpload) {
        await uploadFile(file);
      }
      await onChanged();
      if (skippedCount > 0) {
        setError(
          `写真は${MEDIA_LIMIT}枚までのため、先頭${filesToUpload.length}枚だけアップロードしました。`,
        );
      }
    } catch (uploadError) {
      console.error(uploadError);
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "写真アップロードに失敗しました。",
      );
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    await uploadFiles(files);
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }

    setError(null);
    setCameraError(null);
    try {
      stopCameraStream(cameraStreamRef.current);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);

      requestAnimationFrame(() => {
        if (!cameraVideoRef.current) return;
        cameraVideoRef.current.srcObject = stream;
        cameraVideoRef.current.play().catch(() => {
          setCameraError("カメラの再生を開始できませんでした。");
        });
      });
    } catch (cameraOpenError) {
      console.error(cameraOpenError);
      setCameraError(
        "カメラを起動できませんでした。権限設定を確認してください。",
      );
    }
  };

  const closeCamera = () => {
    setCameraOpen(false);
    stopCameraStream(cameraStreamRef.current);
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const captureFromCamera = async () => {
    if (!cameraVideoRef.current) return;

    setCapturingPhoto(true);
    try {
      const blob = await captureVideoFrame(cameraVideoRef.current);
      const file = new File([blob], `camera-${Date.now()}.jpg`, {
        type: blob.type,
      });
      closeCamera();
      await uploadFiles([file]);
    } catch (cameraCaptureError) {
      console.error(cameraCaptureError);
      setCameraError("写真の取得に失敗しました。");
    } finally {
      setCapturingPhoto(false);
    }
  };

  const onRemove = async (mediaId: string) => {
    setError(null);
    setRemovingId(mediaId);
    try {
      await mediaApi.remove(mediaId);
      await onChanged();
    } catch (removeError) {
      console.error(removeError);
      setError(
        removeError instanceof Error
          ? removeError.message
          : "写真削除に失敗しました。",
      );
    } finally {
      setRemovingId(null);
    }
  };

  const previewUrl = previewingMediaId ? previewUrls[previewingMediaId] : null;
  const previewIndex = previewingMediaId
    ? mediaIds.indexOf(previewingMediaId)
    : -1;
  const hasPreviousPreview = previewIndex > 0;
  const hasNextPreview =
    previewIndex >= 0 && previewIndex < mediaIds.length - 1;

  const movePreview = (direction: -1 | 1) => {
    if (previewIndex < 0) return;
    const nextMediaId = mediaIds[previewIndex + direction];
    if (!nextMediaId) return;
    setPreviewingMediaId(nextMediaId);
  };

  return (
    <div className="meal-log-media">
      {mediaIds.length > 0 && (
        <div className="meal-log-media-list">
          {mediaIds.map((mediaId) => (
            <div className="meal-log-media-card" key={mediaId}>
              {previewUrls[mediaId] ? (
                <button
                  type="button"
                  className="meal-log-media-preview-button"
                  onClick={() => setPreviewingMediaId(mediaId)}
                  aria-label="写真を拡大表示"
                >
                  <img
                    src={previewUrls[mediaId]}
                    alt="食事の写真"
                    className="meal-log-media-image"
                  />
                </button>
              ) : (
                <div className="meal-log-media-placeholder">読み込み中…</div>
              )}
              <button
                type="button"
                className="meal-log-media-remove"
                onClick={() => onRemove(mediaId)}
                disabled={removingId === mediaId}
              >
                {removingId === mediaId ? "削除中…" : "削除"}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="meal-log-media-actions">
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPickFile}
          hidden
        />
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onPickFile}
          hidden
        />
        <button
          type="button"
          className="btn-pearl"
          onClick={openCamera}
          disabled={uploading || hasReachedMediaLimit}
        >
          {uploading ? "送信中…" : "カメラ"}
        </button>
        <button
          type="button"
          className="btn-pearl"
          onClick={() => uploadInputRef.current?.click()}
          disabled={uploading || hasReachedMediaLimit}
        >
          {uploading ? "送信中…" : "アップロード"}
        </button>
      </div>

      {hasReachedMediaLimit && !error && (
        <p className="meal-log-media-limit-note">写真は5枚まで登録できます。</p>
      )}

      {error && <p className="meal-log-media-error">{error}</p>}

      {cameraOpen && (
        <div className="modal-backdrop" role="presentation">
          <div
            className="modal-card camera-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`meal-log-camera-${log.id}`}
          >
            <h2 id={`meal-log-camera-${log.id}`} className="modal-title">
              カメラ
            </h2>
            <video
              ref={cameraVideoRef}
              className="camera-preview"
              autoPlay
              playsInline
              muted
            />
            {cameraError && (
              <p className="meal-log-media-error">{cameraError}</p>
            )}
            <div className="camera-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeCamera}
              >
                閉じる
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={captureFromCamera}
                disabled={capturingPhoto || uploading}
              >
                {capturingPhoto || uploading ? "撮影中…" : "撮影する"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setPreviewingMediaId(null)}
        >
          <div
            className="modal-card meal-log-media-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label="写真プレビュー"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="meal-log-media-preview-header">
              <div className="meal-log-media-preview-nav">
                <button
                  type="button"
                  className="btn-secondary meal-log-media-preview-nav-button"
                  onClick={() => movePreview(-1)}
                  disabled={!hasPreviousPreview}
                >
                  前へ
                </button>
                <button
                  type="button"
                  className="btn-secondary meal-log-media-preview-nav-button"
                  onClick={() => movePreview(1)}
                  disabled={!hasNextPreview}
                >
                  次へ
                </button>
              </div>
              <button
                type="button"
                className="btn-secondary meal-log-media-preview-close"
                onClick={() => setPreviewingMediaId(null)}
              >
                閉じる
              </button>
            </div>
            <img
              src={previewUrl}
              alt="食事の写真を拡大表示"
              className="meal-log-media-preview-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function stopCameraStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

function captureVideoFrame(video: HTMLVideoElement): Promise<Blob> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) {
    throw new Error("Camera stream is not ready");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is not available");
  }

  context.drawImage(video, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to capture camera frame"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.92,
    );
  });
}
